import type { MediaPickerState, MediaPickerViewModel, PickerTab } from "@bundle/core";
import { GIF_COLUMNS, STICKER_COLUMNS } from "@bundle/core";
import { useState } from "react";
import { useLocale, useT, useTr } from "../i18n/I18nProvider.tsx";
import { Button, EmojiPicker, Icon, MediaGrid, Spacer, Stack, Text, TextField, type MediaGridKey, type TextFieldKey } from "../primitives/index.ts";

export type MediaPickerProps = {
  vm: MediaPickerViewModel;
  state: MediaPickerState;
  /** An emoji was picked (the composer inserts it). */
  onEmoji: (emoji: string) => void;
  /** "+ Create sticker"; absent = I can't create here. */
  onCreateSticker?: () => void;
  /** "Add stickers": the packs manager. */
  onManageStickers: () => void;
  /** Bottom sheet on phones: fills the sheet instead of a fixed popover size. */
  fill?: boolean;
  /** A sticker or GIF was queued (e.g. scroll the conversation to the newest message). */
  onSent?: () => void;
  testID: string;
};

const TABS: { tab: PickerTab; label: "picker.tab.emoji" | "picker.tab.stickers" | "picker.tab.gifs" }[] = [
  { tab: "emoji", label: "picker.tab.emoji" },
  { tab: "stickers", label: "picker.tab.stickers" },
  { tab: "gifs", label: "picker.tab.gifs" },
];

/**
 * The composer's picker: Emoji · Stickers · GIFs tabs, each with search, keyboard navigation (↓ from the search field
 * into the grid, arrows inside it, Enter to send, Esc to close) and lazy-loaded images. GIFs come from KLIPY (search
 * field "Search KLIPY", results in KLIPY's order, "Powered by KLIPY").
 */
export function MediaPicker({ vm, state, onEmoji, onCreateSticker, onManageStickers, fill, onSent, testID }: MediaPickerProps) {
  const t = useT();
  const tr = useTr();
  const locale = useLocale();
  const [focusSeq, setFocusSeq] = useState(0);
  const tab = state.picker.tab;
  const query = tab === "stickers" ? state.picker.stickerQuery : state.picker.gifQuery;
  const activeKey = vm.activeKey;

  // Keys from the search field: ↓/↑ move the highlight by rows, Tab to the next cell, Enter sends, Esc closes.
  const onFieldKey = (key: TextFieldKey): boolean => {
    if (key === "escape") {
      vm.close();
      return true;
    }
    if (key === "enter") {
      const sent = vm.pickActive();
      if (sent) onSent?.();
      return sent;
    }
    if (key === "down") vm.move("down");
    else if (key === "up") {
      if (state.picker.active < 0) return false;
      vm.move("up");
    } else if (key === "tab") {
      if (state.picker.active < 0) return false;
      vm.move("right");
    }
    return true;
  };
  // Keys from a focused cell; leaving the grid upwards gives the search field the keyboard back.
  const onGridKey = (key: MediaGridKey) => {
    vm.move(key);
    if (vm.state.picker.active < 0) setFocusSeq((s) => s + 1);
  };
  const field = (placeholder: string, fieldTestID: string) => (
    <Stack paddingX="2" paddingY="1">
      <TextField
        value={query}
        onChange={(v) => vm.setQuery(v)}
        placeholder={placeholder}
        leadingIcon="search"
        autoFocus
        onKey={onFieldKey}
        selection={focusSeq === 0 ? undefined : { start: query.length, end: query.length, seq: focusSeq }}
        testID={fieldTestID}
      />
    </Stack>
  );

  const stickers = (
    <Stack grow gap="1" testID={`${testID}-stickers`}>
      {field(t("picker.stickers.search"), `${testID}-sticker-search`)}
      {state.sections.length === 0 || state.sections.every((s) => s.stickers.length === 0) ? (
        <Stack grow align="center" justify="center" gap="2" padding="5" testID={`${testID}-stickers-empty`}>
          <Icon name="sticker" size="icon.lg" color="text.tertiary" />
          <Text weight="semibold" align="center">
            {state.stickersLoading ? t("common.loading") : query.trim() !== "" ? t("picker.stickers.noResults", { query: query.trim() }) : t("picker.stickers.empty")}
          </Text>
          {query.trim() !== "" || state.stickersLoading ? null : (
            <Text variant="footnote" color="text.secondary" align="center">
              {t("picker.stickers.emptyBody")}
            </Text>
          )}
        </Stack>
      ) : (
        <MediaGrid
          sections={state.sections.map((s) => ({
            key: s.id,
            title: s.title === undefined ? s.name ?? "" : tr(s.title),
            items: s.stickers.map((x) => ({ key: x.id, src: x.url, alt: x.name, width: x.width, height: x.height })),
          }))}
          columns={STICKER_COLUMNS}
          cell="square"
          activeKey={activeKey}
          onSelect={(key) => {
            const sticker = state.sections.flatMap((s) => s.stickers).find((x) => x.id === key);
            if (sticker !== undefined && vm.sendSticker(sticker)) onSent?.();
          }}
          onKey={onGridKey}
          testID={`${testID}-sticker-grid`}
        />
      )}
      <Stack direction="horizontal" align="center" gap="1" paddingX="2" paddingY="1">
        {onCreateSticker === undefined ? null : (
          <Button label={t("picker.stickers.create")} icon="plus" variant="ghost" size="sm" onPress={onCreateSticker} testID={`${testID}-create-sticker`} />
        )}
        <Spacer />
        <Button label={t("picker.stickers.add")} icon="settings" variant="ghost" size="sm" onPress={onManageStickers} testID={`${testID}-manage-stickers`} />
      </Stack>
    </Stack>
  );

  const g = state.gifs;
  const gifItems = g.items.map((r) => ({ key: r.ref.id, src: r.thumbUrl, alt: r.ref.title, width: r.thumbWidth, height: r.thumbHeight }));
  const gifFooter =
    g.status === "loading" || g.status === "loadingMore" ? (
      <Text variant="caption" color="text.tertiary">
        {g.status === "loading" ? t("common.loading") : t("picker.gifs.loadingMore")}
      </Text>
    ) : g.status === "error" ? (
      <Stack align="center" gap="1" testID={`${testID}-gif-error`}>
        <Text variant="footnote" color="status.danger" align="center">
          {g.error === "rateLimited" ? t("picker.gifs.error.rateLimited") : g.error === "network" ? t("picker.gifs.error.network") : t("picker.gifs.error.server")}
        </Text>
        <Button label={t("common.retry")} variant="ghost" size="sm" icon="reload" onPress={() => vm.retryGifs()} testID={`${testID}-gif-retry`} />
      </Stack>
    ) : undefined;
  const gifs =
    g.status === "unavailable" ? (
      <Stack grow align="center" justify="center" gap="2" padding="5" testID={`${testID}-gifs-unavailable`}>
        <Icon name="image" size="icon.lg" color="text.tertiary" />
        <Text weight="semibold" align="center">
          {t("picker.gifs.unavailable")}
        </Text>
        <Text variant="footnote" color="text.secondary" align="center">
          {t("picker.gifs.unavailableBody")}
        </Text>
      </Stack>
    ) : (
      <Stack grow gap="1" testID={`${testID}-gifs`}>
        {field(t("picker.gifs.search"), `${testID}-gif-search`)}
        {g.items.length === 0 && g.status === "ready" ? (
          <Stack grow align="center" justify="center" padding="5">
            <Text color="text.secondary" align="center">
              {t("picker.gifs.empty", { query: g.query })}
            </Text>
          </Stack>
        ) : (
          <MediaGrid
            sections={[
              g.query === "" && g.items.length > 0
                ? { key: "gifs", title: t("picker.gifs.trending"), items: gifItems }
                : { key: "gifs", items: gifItems },
            ]}
            columns={GIF_COLUMNS}
            cell="tile"
            activeKey={activeKey}
            onSelect={(key) => {
              const gif = g.items.find((r) => r.ref.id === key);
              if (gif !== undefined && vm.sendGif(gif)) onSent?.();
            }}
            onKey={onGridKey}
            onEndReached={() => vm.loadMoreGifs()}
            footer={gifFooter}
            testID={`${testID}-gif-grid`}
          />
        )}
        <Stack direction="horizontal" justify="end" paddingX="2.5" paddingY="1">
          <Text variant="caption" color="text.tertiary" testID={`${testID}-powered-by`}>
            {t("picker.gifs.poweredBy")}
          </Text>
        </Stack>
      </Stack>
    );

  const body = (
    <Stack grow testID={testID}>
      <Stack direction="horizontal" align="center" gap="1" paddingX="2" paddingY="1.5">
        {TABS.filter((x) => state.picker.tabs.includes(x.tab)).map((x) => (
          <Button
            key={x.tab}
            label={t(x.label)}
            size="sm"
            variant={x.tab === tab ? "secondary" : "ghost"}
            onPress={() => vm.setTab(x.tab)}
            testID={`${testID}-tab-${x.tab}`}
          />
        ))}
      </Stack>
      {tab === "emoji" ? (
        <Stack grow>
          <EmojiPicker locale={locale} searchPlaceholder={t("picker.emoji.search")} fill onSelect={onEmoji} testID={`${testID}-emoji`} />
        </Stack>
      ) : tab === "stickers" ? (
        stickers
      ) : (
        gifs
      )}
    </Stack>
  );
  if (fill === true) return body;
  return (
    <Stack width="picker.width" height="picker.height">
      {body}
    </Stack>
  );
}
